/**
 * PRODUCT QUIZ POPUP WEB COMPONENT
 * Quiz de recomendación de gafas con filtrado por tags.
 * Flujo: Tipo → Estilo → Rostro (→ forma de montura) → Material
 *
 * Lógica: AND entre grupos de filtro, OR dentro de cada grupo.
 * Cada pregunta tiene un filterKey y sus opciones tienen tags.
 * Al finalizar se envían los filtros activos a product-quiz-results
 * vía Section Rendering API.
 */

class ProductQuizPopup extends HTMLElement {
	constructor() {
		super()

		this.questions = []
		this.currentQuestion = 0
		this.answers = {}
		this.isOpen = false

		this.shadow = null
		this.resultsTrack = null
		this.baseCollection = 'all'
		this.triggerButton = null
		this.showFloatingButton = false
		this.triggerButtonText = 'Encuentra tus gafas ideales'

		this.open = this.open.bind(this)
		this.close = this.close.bind(this)
		this.nextQuestion = this.nextQuestion.bind(this)
		this.previousQuestion = this.previousQuestion.bind(this)
		this.handleOptionClick = this.handleOptionClick.bind(this)
		this.restartQuiz = this.restartQuiz.bind(this)
		this.handleResultsPrev = this.handleResultsPrev.bind(this)
		this.handleResultsNext = this.handleResultsNext.bind(this)
		this.updateResultsCarouselButtons =
			this.updateResultsCarouselButtons.bind(this)
		this._handleExternalOpen = this._handleExternalOpen.bind(this)
	}

	connectedCallback() {
		this.baseCollection = this.getAttribute('base-collection') || 'all'
		this.showFloatingButton =
			this.getAttribute('show-floating-button') === 'true'
		this.triggerButtonText =
			this.getAttribute('trigger-button-text') || 'Encuentra tus gafas ideales'

		this.initializeShadowDOM()
		this.loadQuestions()

		if (this.showFloatingButton) {
			this.createTriggerButton()
		}

		document.addEventListener('quiz:open', this._handleExternalOpen)

		setTimeout(() => {
			this.setupEventListeners()
		}, 50)
	}

	initializeShadowDOM() {
		const template = this.querySelector('template')
		if (!template) return

		this.shadow = this.attachShadow({ mode: 'open' })
		const content = template.content.cloneNode(true)
		this.shadow.appendChild(content)
		template.remove()
	}

	$(selector) {
		return this.shadow ? this.shadow.querySelector(selector) : null
	}

	$$(selector) {
		return this.shadow ? this.shadow.querySelectorAll(selector) : []
	}

	loadQuestions() {
		const script = this.querySelector('.quiz-questions-data')
		if (!script) return

		try {
			this.questions = JSON.parse(script.textContent)
		} catch (e) {
			this.questions = []
		}

		const totalEl = this.$('.quiz-total')
		if (totalEl) {
			totalEl.textContent = this.questions.length
		}
	}

	setupEventListeners() {
		const closeBtn = this.$('.quiz-close')
		if (closeBtn) {
			closeBtn.addEventListener('click', this.close)
		}

		const backBtn = this.$('.quiz-nav-button.back')
		if (backBtn) {
			backBtn.addEventListener('click', this.previousQuestion)
		}

		const nextBtn = this.$('.quiz-nav-button.next')
		if (nextBtn) {
			nextBtn.addEventListener('click', this.nextQuestion)
		}

		const restartBtn = this.$('.quiz-restart-button')
		if (restartBtn) {
			restartBtn.addEventListener('click', this.restartQuiz)
		}

		const resultsPrevBtn = this.$('.quiz-results-nav.prev')
		if (resultsPrevBtn) {
			resultsPrevBtn.addEventListener('click', this.handleResultsPrev)
		}

		const resultsNextBtn = this.$('.quiz-results-nav.next')
		if (resultsNextBtn) {
			resultsNextBtn.addEventListener('click', this.handleResultsNext)
		}

		this.renderQuestions()
	}

	renderQuestions() {
		const container = this.$('.quiz-questions')
		if (!container) return

		container.innerHTML = ''

		if (this.questions.length === 0) {
			container.innerHTML =
				'<p style="text-align: center; padding: 40px;">No hay preguntas configuradas. Configura el quiz desde el Theme Editor.</p>'
			return
		}

		this.questions.forEach((question, qIndex) => {
			const questionEl = document.createElement('div')
			questionEl.className = `quiz-question ${qIndex === 0 ? 'active' : ''}`
			questionEl.dataset.questionIndex = qIndex

			const options = Array.isArray(question.options) ? question.options : []
			const optionsHTML = options
				.map(
					opt => `
				<div class="quiz-option" data-option-id="${opt.id}" data-tags="${opt.tags || ''}" data-filter-key="${question.filterKey || ''}">
					<div class="quiz-option-radio"></div>
					<span>${opt.text}</span>
				</div>
			`,
				)
				.join('')

			const illustrationHTML = question.illustrationUrl
				? `<img class="quiz-illustration" src="${question.illustrationUrl}" alt="Guía visual" loading="lazy">`
				: ''

			questionEl.innerHTML = `
				<h3 class="quiz-question-text">${question.text}</h3>
				${illustrationHTML}
				<div class="quiz-options">${optionsHTML ||
				'<p style="text-align: center; width: 100%;">No hay opciones configuradas para esta pregunta.</p>'
				}</div>
			`

			questionEl.querySelectorAll('.quiz-option').forEach(opt => {
				opt.addEventListener('click', e => this.handleOptionClick(e, qIndex))
			})

			container.appendChild(questionEl)
		})

		this.updateNavigation()
	}

	handleOptionClick(event, questionIndex) {
		const clickedOption = event.currentTarget
		const allOptions = this.$$(
			`.quiz-question[data-question-index="${questionIndex}"] .quiz-option`,
		)

		allOptions.forEach(opt => opt.classList.remove('selected'))
		clickedOption.classList.add('selected')

		const filterKey = clickedOption.dataset.filterKey || ''
		const tags = clickedOption.dataset.tags || ''

		this.answers[questionIndex] = { filterKey, tags }

		// Debug: log selection per question and current answers
		console.log('[Quiz] Selected option Q' + questionIndex + ':', { filterKey, tags })
		console.log('[Quiz] Answers now:', this.answers)

		const nextBtn = this.$('.quiz-nav-button.next')
		if (nextBtn) {
			nextBtn.disabled = false
		}
	}

	nextQuestion() {
		if (this.currentQuestion < this.questions.length - 1) {
			const currentEl = this.$(
				`.quiz-question[data-question-index="${this.currentQuestion}"]`,
			)
			if (currentEl) currentEl.classList.remove('active')

			this.currentQuestion++

			const nextEl = this.$(
				`.quiz-question[data-question-index="${this.currentQuestion}"]`,
			)
			if (nextEl) nextEl.classList.add('active')

			this.updateNavigation()
		} else {
			this.showResults()
		}
	}

	previousQuestion() {
		if (this.currentQuestion > 0) {
			const currentEl = this.$(
				`.quiz-question[data-question-index="${this.currentQuestion}"]`,
			)
			if (currentEl) currentEl.classList.remove('active')

			this.currentQuestion--

			const prevEl = this.$(
				`.quiz-question[data-question-index="${this.currentQuestion}"]`,
			)
			if (prevEl) prevEl.classList.add('active')

			this.updateNavigation()
		}
	}

	updateNavigation() {
		const backBtn = this.$('.quiz-nav-button.back')
		const nextBtn = this.$('.quiz-nav-button.next')
		const currentEl = this.$('.quiz-current')

		if (currentEl) {
			currentEl.textContent = this.currentQuestion + 1
		}

		if (backBtn) {
			backBtn.disabled = this.currentQuestion === 0
		}

		if (nextBtn) {
			const hasAnswer = this.answers[this.currentQuestion] !== undefined
			const isLast = this.currentQuestion === this.questions.length - 1

			nextBtn.textContent = isLast ? 'Ver resultados' : 'Siguiente'
			nextBtn.disabled = !hasAnswer
		}
	}

	/**
	 * Builds filter string from all answers.
	 * Format: filterKey:tag1,tag2;filterKey2:tag3
	 * Empty-tag answers (e.g. "Indiferente") are skipped.
	 */
	buildFilterString() {
		const parts = []

		for (const key of Object.keys(this.answers)) {
			const answer = this.answers[key]
			const tags = (answer && answer.tags ? answer.tags : '').trim().toLowerCase()
			if (!answer || !answer.filterKey || !tags) continue
			if (tags === 'unisex') continue
			parts.push(`${answer.filterKey}:${answer.tags.trim()}`)
		}

		const filterString = parts.join(';')
		console.log('[Quiz] Built filter string:', filterString)
		return filterString
	}

	async showResults() {
		const questionsContainer = this.$('.quiz-questions')
		const footer = this.$('.quiz-footer')
		const loadingEl = this.$('.quiz-loading')
		const resultsEl = this.$('.quiz-results')

		if (questionsContainer) questionsContainer.style.display = 'none'
		if (footer) footer.style.display = 'none'
		if (loadingEl) loadingEl.style.display = 'flex'

		const filters = this.buildFilterString()
		const filterGroups = this.getActiveFilterGroups()

		try {
			// Fetch up to 5 pages (up to 5000 products) in parallel to cover the full catalog
			const fetchPage = async (page) => {
				const params = new URLSearchParams({
					section_id: 'product-quiz-results',
					base: this.baseCollection,
					page: page
				})
				const rootUrl = Shopify.routes.root === '/' ? '' : Shopify.routes.root
				const sectionUrl = `${rootUrl}/?${params.toString()}`
				const response = await fetch(sectionUrl)
				if (!response.ok) return []
				const html = await response.text()
				const doc = new DOMParser().parseFromString(html, 'text/html')
				const resultsContent = doc.querySelector('.quiz-results__products')
				return resultsContent ? Array.from(resultsContent.querySelectorAll('[data-quiz-card]')) : []
			}

			console.log('[Quiz] Fetching multiple pages for full catalog coverage...')
			const pages = await Promise.all([fetchPage(1), fetchPage(2), fetchPage(3), fetchPage(4), fetchPage(5)])
			const allCards = pages.flat()
			console.log('[Quiz] Total candidate cards across all pages:', allCards.length)

			const productsContainer = this.$('.quiz-products')

			if (allCards.length > 0) {
				const firstCard = allCards[0]
				const dbg = {}
				for (const attr of firstCard.attributes) {
					if (attr.name.startsWith('data-')) dbg[attr.name] = attr.value.slice(0, 80)
				}
				console.log('[Quiz] First card data-attrs:', dbg)
			}

			console.log('\n================================================')
			console.log('👓 QUIZ RESULTS EVALUATION 👓')
			console.log('================================================')
			console.log('[Quiz] Opciones elegidas (Filtros activos):', JSON.stringify(filterGroups, null, 2))
			console.log('[Quiz] Total de productos evaluados:', allCards.length)

			const matched = []
			for (const card of allCards) {
				if (this.cardMatchesAllGroups(card, filterGroups)) {
					matched.push(card)
				}
			}

			console.log(`\n[Quiz] Productos con coincidencias exitosas (${matched.length}):`)
			matched.forEach((card, index) => {
				const tags = card.getAttribute('data-tags') || 'Sin tags'
				console.log(`  ✅ Producto #${index + 1} | Tags: ${tags}`)
			})
			console.log('================================================\n')

			const MAX_VISIBLE = 8
			const visible = matched.slice(0, MAX_VISIBLE)

			if (resultsEl && productsContainer) {
				if (visible.length > 0) {
					const newResultsContent = document.createElement('div')
					newResultsContent.className = 'quiz-results__products'
					for (const card of visible) newResultsContent.appendChild(card)
					productsContainer.innerHTML = newResultsContent.outerHTML
					this.initializeResultsCarousel()
					resultsEl.classList.add('active')
					this.trackEvent('quiz_completed', { filters })
				} else {
					productsContainer.innerHTML =
						'<div class="quiz-results__empty"><p>No encontramos gafas que coincidan con tus preferencias. Intenta con otros criterios.</p></div>'
					resultsEl.classList.add('active')
				}
			}

			if (loadingEl) loadingEl.style.display = 'none'
		} catch (error) {
			console.error('[Quiz] Fetch error:', error)
			if (loadingEl) loadingEl.style.display = 'none'
			const productsContainer = this.$('.quiz-products')
			if (productsContainer) {
				productsContainer.innerHTML =
					'<p style="text-align: center;">Hubo un error al cargar los resultados. Por favor intenta nuevamente.</p>'
			}
			if (resultsEl) resultsEl.classList.add('active')
		}
	}

	handleResultsPrev() {
		this.scrollResultsCarousel(-1)
	}

	handleResultsNext() {
		this.scrollResultsCarousel(1)
	}

	initializeResultsCarousel() {
		const productsContainer = this.$('.quiz-products')
		const track = productsContainer
			? productsContainer.querySelector('.quiz-results__products')
			: null

		if (this.resultsTrack) {
			this.resultsTrack.removeEventListener(
				'scroll',
				this.updateResultsCarouselButtons,
			)
		}

		this.resultsTrack = track

		if (this.resultsTrack) {
			this.resultsTrack.addEventListener(
				'scroll',
				this.updateResultsCarouselButtons,
			)
		}

		this.updateResultsCarouselButtons()
	}

	scrollResultsCarousel(direction) {
		if (!this.resultsTrack) return

		const distance = Math.max(this.resultsTrack.clientWidth * 0.9, 220)
		this.resultsTrack.scrollBy({
			left: direction * distance,
			behavior: 'smooth',
		})
	}

	updateResultsCarouselButtons() {
		const prevBtn = this.$('.quiz-results-nav.prev')
		const nextBtn = this.$('.quiz-results-nav.next')

		if (!prevBtn || !nextBtn) return

		if (!this.resultsTrack) {
			prevBtn.disabled = true
			nextBtn.disabled = true
			return
		}

		const maxScrollLeft =
			this.resultsTrack.scrollWidth - this.resultsTrack.clientWidth
		const hasScrollableContent = maxScrollLeft > 8

		if (!hasScrollableContent) {
			prevBtn.disabled = true
			nextBtn.disabled = true
			return
		}

		prevBtn.disabled = this.resultsTrack.scrollLeft <= 4
		nextBtn.disabled = this.resultsTrack.scrollLeft >= maxScrollLeft - 4
	}

	restartQuiz() {
		this.currentQuestion = 0
		this.answers = {}

		const questionsContainer = this.$('.quiz-questions')
		const footer = this.$('.quiz-footer')
		const resultsEl = this.$('.quiz-results')
		const loadingEl = this.$('.quiz-loading')

		if (questionsContainer) questionsContainer.style.display = 'block'
		if (footer) footer.style.display = 'flex'
		if (resultsEl) resultsEl.classList.remove('active')
		if (loadingEl) loadingEl.style.display = 'none'

		this.$$('.quiz-question').forEach((q, i) => {
			q.classList.remove('active')
			if (i === 0) q.classList.add('active')
		})

		this.$$('.quiz-option').forEach(opt => opt.classList.remove('selected'))
		this.updateNavigation()
		this.initializeResultsCarousel()
	}

	open() {
		this.isOpen = true
		const overlay = this.$('.quiz-overlay')
		if (overlay) {
			overlay.classList.add('open')
			document.body.style.overflow = 'hidden'
		}
		this.trackEvent('quiz_opened')
	}

	close() {
		this.isOpen = false
		const overlay = this.$('.quiz-overlay')
		if (overlay) {
			overlay.classList.remove('open')
			document.body.style.overflow = ''
		}
		this.trackEvent('quiz_closed')
	}

	createTriggerButton() {
		const btn = document.createElement('button')
		btn.type = 'button'
		btn.className = 'button quiz-floating-trigger'
		btn.setAttribute('aria-label', this.triggerButtonText)
		btn.innerHTML = `
			<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
				<path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/>
				<circle cx="12" cy="12" r="3"/>
			</svg>
		`
		btn.addEventListener('click', this.open)
		document.body.appendChild(btn)
		this.triggerButton = btn
	}

	_handleExternalOpen() {
		this.open()
	}

	trackEvent(eventName, data = {}) {
		const event = new CustomEvent(eventName, {
			detail: data,
			bubbles: true,
		})
		document.dispatchEvent(event)
		if (window.dataLayer) {
			window.dataLayer.push({ event: eventName, ...data })
		}
	}

	/**
	 * Returns array of active filter groups: [{ key: 'tipo', values: ['gafas de vista'] }, ...]
	 * Skips groups with empty/blank tags ("Indiferente").
	 */
	getActiveFilterGroups() {
		const groups = []
		for (const idx of Object.keys(this.answers)) {
			const a = this.answers[idx]
			const tags = (a && a.tags ? a.tags : '').trim().toLowerCase()
			if (!a || !a.filterKey || !tags) continue
			if (tags === 'unisex') continue
			const values = a.tags
				.split(',')
				.map((v) => v.trim().toLowerCase())
				.filter(Boolean)
			if (values.length === 0) continue
			groups.push({ key: a.filterKey, values })
		}
		return groups
	}

	/**
	 * Bilingual ES<->EN aliases for taxonomy values.
	 */
	expandAliases(value) {
		const v = value.toLowerCase().trim()
		const map = {
			masculino: ['masculino', 'hombre', 'caballero', 'male', 'men', 'man', 'unisex'],
			hombre: ['masculino', 'hombre', 'caballero', 'male', 'men', 'man', 'unisex'],
			caballero: ['masculino', 'hombre', 'caballero', 'male', 'men', 'man', 'unisex'],
			male: ['male', 'men', 'man', 'masculino', 'hombre', 'caballero', 'unisex'],
			men: ['male', 'men', 'man', 'masculino', 'hombre', 'caballero', 'unisex'],
			man: ['male', 'men', 'man', 'masculino', 'hombre', 'caballero', 'unisex'],
			femenino: ['femenino', 'mujer', 'dama', 'female', 'women', 'woman', 'unisex'],
			mujer: ['femenino', 'mujer', 'dama', 'female', 'women', 'woman', 'unisex'],
			dama: ['femenino', 'mujer', 'dama', 'female', 'women', 'woman', 'unisex'],
			female: ['female', 'women', 'woman', 'femenino', 'mujer', 'dama', 'unisex'],
			women: ['female', 'women', 'woman', 'femenino', 'mujer', 'dama', 'unisex'],
			woman: ['female', 'women', 'woman', 'femenino', 'mujer', 'dama', 'unisex'],
			unisex: ['unisex', 'hombre', 'mujer', 'masculino', 'femenino', 'male', 'female'],
			acetato: ['acetato', 'acetate'],
			acetate: ['acetate', 'acetato'],
			metal: ['metal'],
			plastico: ['plastico', 'plástico', 'plastic', 'inyectadas'],
			plástico: ['plastico', 'plástico', 'plastic', 'inyectadas'],
			plastic: ['plastic', 'plastico', 'plástico', 'inyectadas'],
			redondo: ['redondo', 'round', 'redondos'],
			round: ['round', 'redondo', 'redondos'],
			cuadrado: ['cuadrado', 'square', 'cuadradas', 'cuadrados'],
			square: ['square', 'cuadrado', 'cuadradas', 'cuadrados'],
			rectangular: ['rectangular', 'rectangle', 'rectangulares'],
			rectangle: ['rectangle', 'rectangular', 'rectangulares'],
			ovalado: ['ovalado', 'ovalada', 'oval', 'ovalados'],
			ovalada: ['ovalado', 'ovalada', 'oval', 'ovalados'],
			oval: ['oval', 'ovalado', 'ovalada', 'ovalados'],
			aviador: ['aviador', 'aviator'],
			aviator: ['aviator', 'aviador'],
			gato: ['gato', 'ojo de gato', 'cat-eye', 'cat eye'],
			'gafas de sol': ['gafas de sol', 'sol', 'sunglasses', 'sun'],
			'gafas de vista': [
				'gafas de vista',
				'vista',
				'oftálmicas',
				'oftalmicas',
				'eyeglasses',
				'optical',
			],
		}
		return map[v] || [v]
	}

	/**
	 * Returns true if the card matches every active filter group.
	 */
	cardMatchesAllGroups(card, groups) {
		if (groups.length === 0) return true

		for (const group of groups) {
			let attrName
			if (group.key.startsWith('meta--')) {
				attrName = 'data-' + group.key.replace(/--/g, '-')
			} else {
				attrName = `data-meta-${group.key}`
			}

			let haystack = (card.getAttribute(attrName) || '').toLowerCase()
			const fallbackTags = (card.getAttribute('data-tags') || '').toLowerCase()
			const fallbackType = (card.getAttribute('data-type') || '').toLowerCase()

			// Si no encontramos el atributo específico, buscamos en los tags y el tipo de producto como fallback
			if (!haystack) {
				haystack = `${fallbackTags} ${fallbackType}`
			}

			if (!haystack) {
				// Si sigue vacío, entonces no hay dónde buscar, no hay coincidencia
				return false
			}

			let groupMatched = false
			for (const value of group.values) {
				const aliases = this.expandAliases(value)
				for (const alias of aliases) {
					const matched = haystack.includes(alias)
					if (matched) {
						groupMatched = true
						break
					}
				}
				if (groupMatched) break
			}

			if (!groupMatched) {
				// Obtener el título para el log
				const titleEl = card.querySelector('.quiz-card__title, .card-information__text, h3, .card__heading')
				const title = titleEl ? titleEl.textContent.trim() : 'Producto sin título'

				console.log(`  ❌ [${title}] descartado en el grupo "${group.key}".`)
				console.log(`     Motivo: No se encontró ninguno de los valores: [${group.values.join(', ')}]`)
				console.log(`     Contenido evaluado (attr: ${attrName} o tags): "${haystack}"\n`)
				return false
			}
		}
		return true
	}

	disconnectedCallback() {
		if (this.triggerButton) {
			this.triggerButton.removeEventListener('click', this.open)
			this.triggerButton.remove()
			this.triggerButton = null
		}

		document.removeEventListener('quiz:open', this._handleExternalOpen)

		const resultsPrevBtn = this.$('.quiz-results-nav.prev')
		if (resultsPrevBtn) {
			resultsPrevBtn.removeEventListener('click', this.handleResultsPrev)
		}

		const resultsNextBtn = this.$('.quiz-results-nav.next')
		if (resultsNextBtn) {
			resultsNextBtn.removeEventListener('click', this.handleResultsNext)
		}

		if (this.resultsTrack) {
			this.resultsTrack.removeEventListener(
				'scroll',
				this.updateResultsCarouselButtons,
			)
		}
	}
}

if (!window.customElements.get('product-quiz-popup')) {
	window.customElements.define('product-quiz-popup', ProductQuizPopup)
}
